import React from "react";
import { Flex, Text, Heading, Box, SimpleGrid } from "@chakra-ui/react";
import { Image, ImageAutoHeight } from "@/components/common/Image";
import { useTranslation } from "next-i18next";
import LanguageSwitcher from "./Header/LanguageSwitcher";
import { HelpCircle } from "react-feather";
import { HelpChatLink } from "@/consts/help";
import Link from "next/link";
import getConfig from "next/config";

const { publicRuntimeConfig } = getConfig();

type LoginProps = {
  title: string;
  children: React.ReactNode;
};

const Login = ({ title, children }: LoginProps): React.ReactElement => {
  const { t } = useTranslation("common");

  return (
    <Flex
      minHeight="100vh"
      bg={{ xl: "white" }}
      position="relative"
      justifyContent="center"
      align="center"
      px={{ base: ".75rem", md: "2rem" }}
      py=".75rem"
    >
      <Link href={HelpChatLink} passHref>
        <a target="_blank" rel="noreferrer">
          <Flex
            cursor="pointer"
            color="white"
            position="absolute"
            bottom={10}
            left={10}
            zIndex={10}
            alignItems="center"
            gap={2}
          >
            <HelpCircle />
            <Text>{t("help.help")}</Text>
          </Flex>
        </a>
      </Link>
      <Box
        position="absolute"
        top={0}
        left={0}
        height="100%"
        width="100%"
        ml="-48%"
        display={{ base: "none", xl: "block" }}
      >
        <Image
          priority
          src={`${publicRuntimeConfig.basePath}/bg-login-page.svg`}
          objectFit="cover"
          objectPosition="right"
          alt=""
        />
      </Box>
      <Flex
        px={{ base: "1.25rem", md: "2rem", xl: "2.5rem" }}
        py={{ base: "2rem", xl: 0 }}
        maxWidth={{ base: "100%", xl: "1536px" }}
        minHeight={{ xl: "calc(100vh - 1.5rem)" }}
        w={{ base: "500px", xl: "100%" }}
        backgroundColor={{ base: "white", xl: "transparent" }}
        borderRadius={{ base: ".375rem", xl: 0 }}
      >
        <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="1rem" w="100%">
          <Flex
            direction="column"
            position="relative"
            display={{ base: "none", xl: "flex" }}
          >
            <Box width="162px" height="40px" mt="1.25rem" position="relative">
              <Image
                priority
                src={`${publicRuntimeConfig.basePath}/logo.svg`}
                objectFit="contain"
                objectPosition="left"
                alt=""
              />
            </Box>
            <Flex flex={1} direction="column" justifyContent="center">
              <Box w="70%" ml="3.5rem" mt="-4rem">
                <ImageAutoHeight
                  priority
                  layout="fill"
                  objectFit="cover"
                  src={`${publicRuntimeConfig.basePath}/login.svg`}
                  alt=""
                />
              </Box>
              <Text
                fontWeight="500"
                lineHeight="1.25"
                fontSize="2.25rem"
                mt="2.5rem"
                color="white"
              >
                {t("users.loginSlogan.title")}
              </Text>
              <Text
                fontSize="1.125rem"
                mt="1.25rem"
                color="white"
                opacity="0.7"
              >
                {t("users.loginSlogan.subtitle")}
              </Text>
            </Flex>
          </Flex>
          <Flex align="center" ml={{ xl: "5rem" }}>
            <Box width={{ base: "100%", xl: "400px" }} ml={{ xl: "8rem" }}>
              <Flex
                mb="32px"
                justifyContent="space-between"
                alignItems="center"
              >
                <Heading
                  as="h2"
                  mb="0"
                  fontSize={["2xl", null, null, null, "3xl"]}
                >
                  {title}
                </Heading>
                <LanguageSwitcher />
              </Flex>
              {children}
            </Box>
          </Flex>
        </SimpleGrid>
      </Flex>
    </Flex>
  );
};

export default Login;
